import type { Block } from '@/api/courses';
import { openItemsAnswerText } from './openItems';

/**
 * Текст ответа ученика для кнопки «Скопировать» в разборе урока (куратор и админ, 13.10.2026).
 *
 * Кнопка есть только там, где ученик НАПИСАЛ текст: сочинение и задания с вводом слова. Выбор варианта,
 * сортировка, сопоставление и запись голоса текстом не являются — копировать там нечего, и кнопки нет (`null`).
 *
 * ⚠️ ПОЛЯ КОРОТКИХ ОТВЕТОВ В РАЗБОРЕ НЕ ВЫДЕЛЯЮТСЯ — И ЭТО НАМЕРЕННО. Для выделения полю нужны события мыши и
 * касания, а с ними куратор смог бы печатать в поле ученика. Ответ на сервер не уйдёт (`onAnswer` в разбор не
 * передаётся), но экран показывал бы не то, что ответил ученик. Поэтому весь ответ отдаёт кнопка.
 */

/** Задания с вводом слова: один ответ — строка, несколько — JSON-массив (как `savedItemValues` в blockRenderers). */
const TYPED_ITEMS = new Set(['word_input_gap', 'word_input_image', 'anagram']);

/** Ответы нескольких пропусков — нумерованными строками; пустой пропуск — тире, чтобы номера не съехали. */
function numbered(values: unknown[]): string | null {
  const lines = values.map((v) => (typeof v === 'string' ? v.trim() : ''));
  if (lines.every((v) => v === '')) return null;
  return lines.map((v, i) => `${i + 1}. ${v || '—'}`).join('\n');
}

function parseArray(raw: string): unknown[] | null {
  try {
    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

export function reviewAnswerText(
  block: Pick<Block, 'type' | 'content'>,
  saved: { answer_data: string } | undefined,
): string | null {
  if (!saved) return null;
  const raw = saved.answer_data ?? '';

  if (block.type === 'essay') return raw.trim() === '' ? null : raw;

  // «Ответ по пунктам» (13.10.2026) — нумерованными строками, как в сдаче у куратора (progress.open_items_text).
  if (block.type === 'open_items') return openItemsAnswerText(raw);

  if (TYPED_ITEMS.has(block.type)) {
    // Одиночное задание хранит ответ строкой как есть, несколько — JSON-массивом.
    const arr = parseArray(raw);
    if (!arr) return raw.trim() === '' ? null : raw;
    if (arr.length > 1) return numbered(arr);
    const only = arr[0];
    return typeof only === 'string' && only.trim() !== '' ? only : null;
  }

  // «Дополни таблицу» (13.10.2026) — те же пропуски, разложенные по ячейкам: ответ — массив по порядку
  // пропусков (lib/tableFill.ts), банк слов — выбор, как перетаскивание у gap_fill.
  if (block.type === 'gap_fill' || block.type === 'table_fill') {
    // Выпадающий список и перетаскивание — выбор из готовых слов, а не написанный текст.
    const mode = block.content?.mode;
    if (typeof mode === 'string' && mode !== '' && mode !== 'input') return null;
    const arr = parseArray(raw);
    return arr ? numbered(arr) : null;
  }

  return null;
}
